import { computed, makeObservable } from 'mobx'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

const TriggerTypes = {
  repeat: 'repeat',
  beforeAfter: 'before-after',
}

const Slugs = {
  documentCreated: 'DOCUMENT_CREATED',
  documentUpdated: 'DOCUMENT_UPDATED',
  documentMadePublic: 'DOCUMENT_MADE_PUBLIC',
  employeeCreated: 'EMPLOYEE_CREATED',
  employeeTerminated: 'EMPLOYEE_TERMINATED',
  employeeUnterminated: 'EMPLOYEE_UNTERMINATED',
  employeeUpdated: 'EMPLOYEE_UPDATED',
  leaveCreated: 'LEAVE_CREATED',
  leaveUpdated: 'LEAVE_UPDATED',
  leaveRejected: 'LEAVE_REJECTED',
  leaveApproved: 'LEAVE_APPROVED',
  contractUpdated: 'CONTRACT_UPDATED',
  contractCreated: 'CONTRACT_CREATED',
  applicationCreated: 'APPLICATION_CREATED',
  applicationDisqualified: 'APPLICATION_DISQUALIFIED',
  contractVersionUpdated: 'CONTRACT_VERSION_UPDATED',
  contractVersionCreated: 'CONTRACT_VERSION_CREATED',
  schemaBasicInfo: 'SCHEMA_BASIC_INFO',
  schemaWorkSchedule: 'SCHEMA_WORK_SCHEDULE',
  schemaGrossSalary: 'SCHEMA_GROSS_SALARY',
  schemaContractInfo: 'SCHEMA_CONTRACT_INFO',
  schemaContract: 'SCHEMA_CONTRACT',
  customFieldsValueUpdated: 'CUSTOM_FIELDS_VALUE_UPDATED',
  costCenterMembershipCreated: 'COST_CENTER_MEMBERSHIP_CREATED',
}

const DeprecatedTriggers = [
  Slugs.contractCreated,
  Slugs.contractUpdated,
  Slugs.schemaBasicInfo,
  Slugs.schemaWorkSchedule,
  Slugs.schemaGrossSalary,
  Slugs.schemaContractInfo,
  Slugs.schemaContract,
]

const ContentKinds = {
  main: 'main',
  yearly: 'TIME_TRIGGER_YEARLY',
  trimester: 'TIME_TRIGGER_TRIMESTER',
  monthly: 'TIME_TRIGGER_MONTHLY',
  weekly: 'TIME_TRIGGER_WEEKLY',
  hourly: 'TIME_TRIGGER_HOURLY',
  daily: 'TIME_TRIGGER_DAILY',
}

const SCHEMA_ROOT = 'SCHEMA'
interface ITrigger {
  id: string
  slug: string
  name: string
  label: string | null
  description: string
  attributes: string[]
  type: string | null
}

export interface ITriggerConditionOption {
  label: string
  value: string
}

export interface ITriggerCondition {
  actionable_type: string
  key: string
  label: string
  operations: ITriggerOperation[]
  show_has_changed: boolean
  type: string
  options?: ITriggerConditionOption[]
  num_values?: {
    min_value: number
    max_value: number
  }
}

export interface ITriggerOperation {
  id: number
  attribute: string
  operator: string
}

export interface ITriggerConditions {
  label: string
  conditions: ITriggerCondition[]
}

export interface ITriggerWithConditions {
  attributes: string[]
  conditions?: ITriggerConditions[]
  description: string
  label: string
  name: string
  slug: string
  type: string
}

export class Trigger extends Model<ITrigger> {
  static urlRoot() {
    return '/automations/triggers'
  }

  // TODO: domain_extension -> Remove them when all triggers are migrated
  static BooleanAttributes = ['has_payroll']
  static DateAttributes = ['birthday', 'starts_on', 'ends_on', 'trial_period_ends_on']
  static ContentKinds = ContentKinds
  static Slugs = Slugs
  static Types = TriggerTypes

  constructor(attr: ITrigger) {
    super(attr)
    makeObservable(this, {
      name: computed,
      isRepeat: computed,
      isBeforeAfter: computed,
      isRecurrent: computed,
      recurrentNeedsMoreConfig: computed,
      isCustomSchemaTrigger: computed,
      isDeprecated: computed,
    })
  }

  get name(): string {
    if (this.isCustomSchemaTrigger) {
      return i18n.t('automations.triggers.schema_value_updated', {
        label: this.attributes.label,
      })
    }

    return this.attributes.description
  }

  matches(str: string): boolean {
    const slugString = i18n.t('automations.triggers.schema_value_updated', {
      label: this.attributes.label,
    })

    const escapedPattern = str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const descriptionString = this.attributes.description

    const matchSlug = this.isCustomSchemaTrigger && new RegExp(escapedPattern, 'i').test(slugString)

    const matchDescription =
      this.has('description') && new RegExp(escapedPattern, 'i').test(descriptionString)

    return matchSlug || matchDescription
  }

  get isRepeat(): boolean {
    return this.attributes.type === TriggerTypes.repeat
  }

  get isBeforeAfter(): boolean {
    return this.attributes.type === TriggerTypes.beforeAfter
  }

  get isRecurrent(): boolean {
    return (
      this.attributes.slug === ContentKinds.hourly ||
      this.attributes.slug === ContentKinds.daily ||
      this.attributes.slug === ContentKinds.weekly ||
      this.attributes.slug === ContentKinds.monthly ||
      this.attributes.slug === ContentKinds.trimester ||
      this.attributes.slug === ContentKinds.yearly
    )
  }

  get recurrentNeedsMoreConfig(): boolean {
    return (
      this.attributes.slug === ContentKinds.weekly ||
      this.attributes.slug === ContentKinds.monthly ||
      this.attributes.slug === ContentKinds.trimester ||
      this.attributes.slug === ContentKinds.yearly
    )
  }

  get isCustomSchemaTrigger(): boolean {
    return this.attributes.slug.startsWith(SCHEMA_ROOT)
  }

  get isDeprecated(): boolean {
    return !!DeprecatedTriggers.find((slug) => slug === this.attributes.slug)
  }
}
